"use client";

import { Box, HStack, Stack, Table } from "@chakra-ui/react";
import { CustomPagination } from "../components/ui/custom-pagination.tsx";
import { useQuery } from "@tanstack/react-query";
import Loader from "../components/loader.tsx";
import { useNavigate } from "react-router-dom";
import { materialTypeMapper } from "../utils/mappers/material-type.mapper.ts";
import { api } from "../api/custom-api.ts";
import { plateTypeMapper } from "../utils/mappers/plate-type.mapper.ts";
import type { MaterialTypeEnum } from "@ns/types";
import ControlledForm from "../components/form/controlled-form.tsx";
import { type SearchSchema, searchSchema } from "../schemas/search.schema.ts";
import { useFormContext } from "react-hook-form";
import ControlledInput from "../components/form/controlled-input.tsx";
import { usePagination } from "../hooks";

const ChooseMaterial = () => {
  const { watch } = useFormContext<SearchSchema>();
  const search = watch("search") || "";

  const pageSize = 10;

  const navigate = useNavigate();

  const {
    pageIndex,
    setPage,
    nextPage: onNextPage,
    previousPage: onPreviousPage,
    canPreviousPage,
  } = usePagination({
    pageType: "chooseCodebook",
    search,
    pageSize,
  });

  const { data, isLoading } = useQuery(
    ["codebookWarehouse", pageIndex, pageSize, search],
    () =>
      api.codebookWarehouse.getCodebookWarehouse(
        pageSize,
        pageIndex * pageSize,
        search,
      ),
    {
      keepPreviousData: true,
    },
  );

  // Calculate canNextPage based on data
  const canNextPage = data ? pageIndex < data.totalPages - 1 : false;

  const onClick = (id: string) => {
    navigate(id);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      w="100%"
      h="full"
      pt={["2rem", "3rem", "5.25rem"]}
      p={["0.5rem", "0.75rem", "1rem"]}
      bg="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <HStack
        w="full"
        justify="space-between"
        pt={["2rem", "3rem", "5.25rem"]}
        mb="1rem"
        flexWrap="wrap"
      >
        <Box fontSize={["lg", "xl", "2xl"]} fontWeight="semibold" color="black">
          IZABERITE MATERIJAL
        </Box>
        <ControlledInput
          name="search"
          // @ts-ignore
          w={["100%", "20rem"]}
          placeholder="Pretraži"
        />
      </HStack>
      <Stack width="full" gap="5">
        <Box overflowX="auto" w="100%" borderRadius="md" boxShadow="sm">
          <Table.Root
            size="sm"
            stickyHeader
            interactive
            variant="outline"
            minWidth="800px"
          >
            <Table.Header bg="gray.200">
              <Table.Row>
                <Table.ColumnHeader
                  key={1}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  ŠIFRA MATERIJALA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={2}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  NAZIV MATERIJALA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={3}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  TIP MATERIJALA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={4}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  TIP PLOČE
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data?.items?.map((item) => (
                <Table.Row
                  key={item.id}
                  onClick={() => onClick(item.id)}
                  className="hover:bg-gray-100 cursor-pointer"
                >
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.code}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.name}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {materialTypeMapper(item.type as MaterialTypeEnum)}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {plateTypeMapper(item.plateType)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        {/* Pagination Controls */}
        <CustomPagination
          count={data!.totalItems}
          pageSize={pageSize}
          currentPage={pageIndex}
          onPageChange={setPage}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          siblingCount={1}
        />
      </Stack>
    </Box>
  );
};

export const ChooseMaterialPage = () => {
  return (
    <ControlledForm schema={searchSchema}>
      <ChooseMaterial />
    </ControlledForm>
  );
};
